import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import * as A from "../../../store/actions/orgChart/orgChartActions";
import OrgChartTree, { OrgChartList } from "../../../components/Dashboard/OrgChart/OrgChartTree";
import { formatDate, errorText } from "../../../components/Dashboard/OrgChart/orgChartModel";
import { AddPanel, SelectedPanel, GroupsPanel, LevelPanel, DetailRowsPanel } from "./EditorPanels";

/**
 * HR: edit one chart's draft (approved design v4-people.html). Chart on the left with the same
 * drawing staff see (plus "Left the company" flags), panels on the right. Draft bar on top:
 * Discard draft / Publish. Photos come from EVOX's saved copy when people are added, or an HR upload.
 */
class OrgChartEditor extends Component {
  constructor(props) {
    super(props);
    this.state = { selectedId: null, closed: {}, renaming: false, name: "", error: null };
  }

  componentDidMount() {
    this.props.fetchDraft(this.props.chartId);
  }

  componentDidUpdate(prev) {
    if (prev.chartId !== this.props.chartId) {
      this.setState({ selectedId: null, closed: {} });
      this.props.fetchDraft(this.props.chartId);
    }
  }

  id() {
    return this.props.chartId;
  }

  /** Tree items plus side-group items (tagged with their group) for selection lookups. */
  allItems(draft) {
    return draft.items.concat(...draft.groups.map((g) => g.items.map((i) => ({ ...i, group: g.id }))));
  }

  rename = (e) => {
    e.preventDefault();
    this.props.renameChart(this.id(), this.state.name.trim())
      .then(() => this.setState({ renaming: false, error: null }))
      .catch((err) => this.setState({ error: err && err.status === 422 ? errorText(err) : null }));
  };

  publish = () => this.props.publishChart(this.id()).catch(() => null);

  discard = () => {
    if (window.confirm("Throw away every change since the last publish?")) this.props.discardDraft(this.id()).catch(() => null);
  };

  render() {
    const { draft, draftError } = this.props;
    const s = this.state;

    if (draftError) {
      return <div className="org-chart"><p className="oc-state" role="status">This chart could not be loaded. <Link to={global.links.org_chart_manage}>Back to charts</Link></p></div>;
    }
    if (!draft || draft.chart.id !== this.id()) {
      return <div className="org-chart"><p className="oc-state" role="status" aria-busy="true"><i className="fa fa-spinner fa-spin" aria-hidden="true" />Loading the chart…</p></div>;
    }

    const all = this.allItems(draft);
    const selected = all.find((i) => i.id === s.selectedId) || null;
    const leavers = draft.items.filter((i) => i.left).length;
    const status = draft.chart.status;

    return (
      <div className="org-chart">
        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
          {s.renaming ? (
            <form className="d-flex align-items-center" style={{ gap: 6 }} onSubmit={this.rename}>
              <input className="form-control" aria-label="Chart name" maxLength={80} value={s.name} onChange={(e) => this.setState({ name: e.target.value })} autoFocus />
              <button type="submit" className="btn btn-primary">Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => this.setState({ renaming: false, error: null })}>Cancel</button>
            </form>
          ) : (
            <h1 className="oc-page-title">
              Edit chart: {draft.chart.name}{" "}
              <button type="button" className="oc-act" onClick={() => this.setState({ renaming: true, name: draft.chart.name })} aria-label="Rename chart"><i className="fa fa-pencil" aria-hidden="true" /></button>
            </h1>
          )}
          <Link to={global.links.org_chart_manage} className="btn btn-secondary"><i className="fa fa-arrow-left" aria-hidden="true" />Back to charts</Link>
        </div>
        {s.error && <div className="invalid-feedback d-block mb-2" role="alert">{s.error}</div>}

        <div className={"oc-draftbar" + (status === "published" ? " clean" : "")}>
          <span className={"badge " + (status === "published" ? "oc-badge-ok" : "badge-warning")}>{status === "published" ? "Published" : "Draft"}</span>
          <span>
            {status === "not_published" && "Not published yet. Staff don't see this chart."}
            {status === "draft_changes" && "Staff still see the version published on " + formatDate(draft.chart.published_at) + "."}
            {status === "published" && "Staff see exactly this version."}
            {leavers > 0 && <> <b>{leavers === 1 ? "1 person has" : leavers + " people have"} left the company</b> and {leavers === 1 ? "is" : "are"} marked in red.</>}
          </span>
          <span className="ml-auto" />
          {status === "draft_changes" && <button type="button" className="btn btn-secondary btn-sm" onClick={this.discard}>Discard draft</button>}
          {status !== "published" && <button type="button" className="btn btn-primary btn-sm" onClick={this.publish}><i className="fa fa-check" aria-hidden="true" />Publish</button>}
        </div>

        <div className="oc-editor">
          <div className="oc-card">
            {draft.items.length === 0 && draft.groups.length === 0 ? (
              <div className="oc-empty"><i className="fa fa-sitemap" aria-hidden="true" /><h3>This chart is empty</h3><p>Use "Add to chart" to put the first people at the top level.</p></div>
            ) : (
              <>
                <div className="oc-canvas">
                  <OrgChartTree items={draft.items} groups={draft.groups} detailRows={draft.chart.detail_rows || []} selectedId={s.selectedId} closed={s.closed} showLeft
                    onSelect={(i) => this.setState((st) => ({ selectedId: st.selectedId === i.id ? null : i.id }))}
                    onToggle={(id) => this.setState((st) => ({ closed: { ...st.closed, [id]: !st.closed[id] } }))}
                    onOpenChart={() => null} chartId={draft.chart.id} />
                </div>
                <OrgChartList items={draft.items} groups={draft.groups} detailRows={draft.chart.detail_rows || []} closed={s.closed} showLeft
                  onToggle={(id) => this.setState((st) => ({ closed: { ...st.closed, [id]: !st.closed[id] } }))}
                  onSelect={(i) => this.setState({ selectedId: i.id })} />
              </>
            )}
            <p className="text-muted mb-0 px-3 py-2" style={{ fontSize: 11 }}>Select a box to edit it, or to add people under it.</p>
          </div>
          <div>
            <AddPanel items={draft.items} groups={draft.groups} selected={selected} searchEmployees={this.props.searchEmployees}
              onAdd={(data) => this.props.addItems(this.id(), data)} />
            {selected && (
              <SelectedPanel item={selected} items={draft.items} detailRows={draft.chart.detail_rows || []}
                onUpdate={(itemId, data) => this.props.updateItem(this.id(), itemId, data)}
                onRemove={(itemId, children) => this.props.removeItem(this.id(), itemId, children).then(() => this.setState({ selectedId: null }))}
                onUpload={(itemId, fd) => this.props.uploadPhoto(this.id(), itemId, fd)} />
            )}
            <DetailRowsPanel rows={draft.chart.detail_rows || []} onSave={(rows) => this.props.saveDetailRows(this.id(), rows)} />
            <GroupsPanel groups={draft.groups}
              onAdd={(name) => this.props.addGroup(this.id(), name)}
              onRename={(gid, name) => this.props.updateGroup(this.id(), gid, { name })}
              onDelete={(gid) => this.props.deleteGroup(this.id(), gid)} />
            <LevelPanel items={draft.items} onRemoveLevel={(level) => this.props.removeLevel(this.id(), level).catch(() => null)} />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  draft: state.orgChart && state.orgChart.draft,
  draftError: !!(state.orgChart && state.orgChart.draftError),
});

const mapDispatchToProps = (dispatch) => ({
  fetchDraft: (id) => dispatch(A.fetchDraft(id)),
  renameChart: (id, name) => dispatch(A.renameChart(id, name)),
  saveDetailRows: (id, rows) => dispatch(A.saveDetailRows(id, rows)),
  addItems: (id, data) => dispatch(A.addItems(id, data)),
  updateItem: (id, itemId, data) => dispatch(A.updateItem(id, itemId, data)),
  removeItem: (id, itemId, children) => dispatch(A.removeItem(id, itemId, children)),
  removeLevel: (id, level) => dispatch(A.removeLevel(id, level)),
  uploadPhoto: (id, itemId, fd) => dispatch(A.uploadPhoto(id, itemId, fd)),
  addGroup: (id, name) => dispatch(A.addGroup(id, name)),
  updateGroup: (id, gid, data) => dispatch(A.updateGroup(id, gid, data)),
  deleteGroup: (id, gid) => dispatch(A.deleteGroup(id, gid)),
  publishChart: (id) => dispatch(A.publishChart(id)),
  discardDraft: (id) => dispatch(A.discardDraft(id)),
  searchEmployees: (q) => dispatch(A.searchEmployees(q)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrgChartEditor);
